import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import Caption from 'LuxKit/Typography/Caption'
import type { FocusEventHandler } from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import RecentSearchSuggestions from '../SearchForm/SearchLocationInput/RecentSearchSuggestions'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import SearchPlaceSelectList from './SearchPlaceSelectList'
import styled from 'styled-components'
import AIGlowBorder from 'components/Search/SearchForm/AIGlowBorder'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import TextAreaOnly from 'components/Common/Form/Input/TextAreaOnly'
import IconButton from 'LuxKit/Button/IconButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import {
  getLocationSearchErrorMessage,
} from 'constants/search'
import { rem } from 'polished'
import SearchTextListItem from '../SearchTextListItem/SearchTextListItem'
import useRecommendationSuggestions from 'hooks/Search/useRecommendationSuggestions'

/* Extended AIGlowBorder with additional margin and box-shadow */
const StyledAIGlowBorder = styled(AIGlowBorder)`
  margin-top: ${rem(9)};
  box-shadow: 0 0 ${rem(6)} ${props => props.theme.palette.product.ai.light35},
              0 0 ${rem(12)} ${props => props.theme.palette.product.ai.dark25};
`

const StyledTextAreaOnly = styled(TextAreaOnly)`
  border: none;
  border-radius: ${rem(2)};
  background: white;
  position: relative;
  z-index: 1;
`

const PlaceControl = styled(VerticalSpacer)`
  position: relative;
  width: 100%;
  height: ${rem(46)};
`

const SearchItemContainer = styled.div`
  margin-top: ${rem(8)};
`

interface Props {
  className?: string
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  value?: App.SearchItem
  required?: boolean
  placeholder: string
  localInputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>
  searchPhrase: string
  onClearClick: () => void
  finalOpen: boolean
  showPlaceholder: boolean
  hasRecentSearchItems: boolean
  recentSearches: Array<App.FullRecentSearch>
  selectedDropdown: number | undefined
  onRecentSearchDelete: (item: App.FullRecentSearch) => void
  onRecentSearchClick: (item: App.FullRecentSearch) => void
  placeholderTitle?: string
  onLocationSelect: (
    searchItem?: App.SearchItem,
    mouseEvent?: React.MouseEvent<HTMLElement>,
  ) => void
  placeTypes?: Array<App.SearchPlaceType>
  searchTypes?: Array<App.SearchType>
  searchVertical?: App.SearchPlaceVertical
  overrideSearchItems?: Array<App.SearchItem>
  setSearchPhrase: React.Dispatch<React.SetStateAction<string>>
  placeholderItems: Array<App.SearchItem>
  placeholdersSearches: Array<App.SearchItem>
  handleResultsChange?: (
    results: Array<App.SearchItem>,
    searchPhrase: string,
    shouldTrack?: boolean,
    loading?: boolean,
  ) => void
  showHotelImagesLayout?: boolean
  multiline?: boolean
  showNaturalLanguageSearchSuggestions?: boolean
  /** Temporary: TypeaheadId to use for streaming (passed from parent to ensure consistency) */
  temporaryTypeaheadId?: string
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
}

function SearchPlaceSelectInline(props: Props) {
  const intl = useIntl()
  const {
    className,
    onKeyDown,
    value,
    required,
    placeholder,
    localInputRef,
    searchPhrase,
    showPlaceholder,
    hasRecentSearchItems,
    recentSearches,
    selectedDropdown,
    onRecentSearchDelete,
    onRecentSearchClick,
    placeholderTitle,
    onLocationSelect,
    placeTypes,
    searchTypes,
    searchVertical,
    overrideSearchItems,
    setSearchPhrase,
    onClearClick,
    placeholderItems,
    handleResultsChange,
    multiline = false,
    showNaturalLanguageSearchSuggestions = false,
    temporaryTypeaheadId,
    temporarySearchSubmitRef,
  } = props

  const handleOnClick = useCallback((searchItem?: App.SearchItem) => {
    if (!searchItem) {
      onLocationSelect(searchItem)
      return
    }

    const itemIndex = placeholderItems.findIndex(item => item.value === searchItem.value)
    if (itemIndex === -1) {
      onLocationSelect(searchItem)
      return
    }

    onLocationSelect(searchItem)
  }, [onLocationSelect, placeholderItems])

  useEffect(() => {
    localInputRef.current?.focus({ preventScroll: true })
  }, [localInputRef])

  const handleFocus = useCallback<FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>((event) => {
    if (event.currentTarget.value) {
      event.currentTarget.select()
    }
  }, [])

  const recommendationSuggestions = useRecommendationSuggestions()

  const naturalLanguageResults = useMemo(() => {
    if (recommendationSuggestions.length > 0) return recommendationSuggestions
    return placeholderItems.filter(item => item.searchType === 'searchItemV3').filter(item => item.display.searchType === 'naturalLanguage')
  }, [placeholderItems, recommendationSuggestions])

  const nonNaturalLanguageResults = useMemo(() => {
    return placeholderItems.filter(item => {
      if (item.searchType === 'searchItemV3') {
        return item.display.searchType !== 'naturalLanguage'
      }
      return true
    })
  }, [placeholderItems])

  return (<PlaceControl gap={12} className={className}>
    {!!required && <HiddenInput
      value={value ? 'filled' : undefined}
      required
      invalidMessage={getLocationSearchErrorMessage(intl)}
    />}
    {multiline && (
      <StyledAIGlowBorder>
        <StyledTextAreaOnly
          noValidationMessage
          noValidationSpacing
          resize="none"
          placeholder={placeholder}
          onFocus={handleFocus}
          ref={localInputRef as React.RefObject<HTMLTextAreaElement>}
          defaultValue={value?.format.mainText}
          onChange={e => setSearchPhrase(e.currentTarget.value)}
          onKeyDown={onKeyDown}
          endIcon={searchPhrase.length > 0 ?
              <IconButton kind="tertiary" onClick={onClearClick}><LineTimesIcon /></IconButton> :
              <LineSearchIcon />}
        />
      </StyledAIGlowBorder>
    )}
    {!multiline && (
      <TextInputOnly
        noValidationSpacing
        placeholder={placeholder}
        onFocus={handleFocus}
        ref={localInputRef as React.RefObject<HTMLInputElement>}
        defaultValue={value?.format.mainText}
        onChange={e => setSearchPhrase(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        endIcon={searchPhrase.length > 0 ?
            <IconButton kind="tertiary" onClick={onClearClick}><LineTimesIcon /></IconButton> :
            <LineSearchIcon />}
      />
    )}
    {searchVertical === 'hotel' &&
      <>
        {showPlaceholder && <VerticalSpacer gap={12}>
          {hasRecentSearchItems && <RecentSearchSuggestions
            recentSearches={recentSearches}
            onDelete={onRecentSearchDelete}
            selectedDropdown={selectedDropdown}
            onRecentSearchClick={onRecentSearchClick}
            iconBackground
            horizontal={false}
          />}
          {showNaturalLanguageSearchSuggestions && naturalLanguageResults.length > 0 &&
            <>
              <Caption variant="large" weight="bold">{intl.formatMessage({
                id: 'VwpUK7uptFpINmtuClGY',
                defaultMessage: 'Suggested searches',
                description: 'Heading for natural language suggested searches in inline search dropdown',
              })}
              </Caption>
              {naturalLanguageResults.map(
                (suggestion, index) =>
                  <SearchTextListItem
                    key={`${suggestion.value}_${index}`}
                    searchItem={suggestion}
                    onClick={handleOnClick}
                  />,
              )}
            </>}
          {placeholderTitle && <Caption variant="large" weight="bold">{placeholderTitle}</Caption>}
          <SearchItemContainer>
            {nonNaturalLanguageResults.map(item => <SearchMenuListItem
              key={item.value}
              searchItem={item}
              onClick={handleOnClick}
              imageId={item.imageId}
              searchVertical={searchVertical}
              useImage
            />)}
          </SearchItemContainer>
        </VerticalSpacer>}
        {!showPlaceholder && <SearchPlaceSelectList
          searchPhrase={searchPhrase}
          onResultsChange={handleResultsChange}
          onChange={onLocationSelect}
          placeTypes={placeTypes}
          searchTypes={searchTypes}
          vertical={searchVertical}
          overrideSearchItems={overrideSearchItems}
          useImage
          showNaturalLanguageSearchSuggestions={showNaturalLanguageSearchSuggestions}
          temporaryTypeaheadId={temporaryTypeaheadId}
          temporarySearchSubmitRef={temporarySearchSubmitRef}
        />}
      </>}
    {searchVertical !== 'hotel' &&
      <>
        {showPlaceholder && <VerticalSpacer gap={12}>
          {hasRecentSearchItems && <>
            <RecentSearchSuggestions
              recentSearches={recentSearches}
              onDelete={onRecentSearchDelete}
              selectedDropdown={selectedDropdown}
              onRecentSearchClick={onRecentSearchClick}
            />
            <Divider kind="primary" />
          </>}
          {placeholderTitle && <Caption variant="large" weight="bold">{placeholderTitle}</Caption>}
          <div>
            {placeholderItems.map(item => <SearchMenuListItem
              key={item.value}
              searchItem={item}
              onClick={handleOnClick}
              searchVertical={searchVertical}
              useImage={searchVertical === 'tour'}
            />)}
          </div>
        </VerticalSpacer>}
        {!showPlaceholder && <SearchPlaceSelectList
          searchPhrase={searchPhrase}
          onResultsChange={handleResultsChange}
          onChange={onLocationSelect}
          placeTypes={placeTypes}
          searchTypes={searchTypes}
          vertical={searchVertical}
          overrideSearchItems={overrideSearchItems}
          useImage={searchVertical === 'tour'}
          temporaryTypeaheadId={temporaryTypeaheadId}
          temporarySearchSubmitRef={temporarySearchSubmitRef}
        />}
      </>}
  </PlaceControl>
  )
}

export default SearchPlaceSelectInline
